import { Scene } from 'phaser';
import FontFaceObserver from 'fontfaceobserver';
import EDC from '@/components/games/shared/globals';
import store from '@/store';
import GradeScene from './GradeSelection/main';
import BackgroundPreloader from '@/components/games/shared/preloader-background';
import AvatarsMainScene from './AvatarSelection/main';
import GenderMainScene from './GenderSelection/main';
import NameMainScene from './NameSelection/main';
import SummaryMainScene from './ProfileSummary/main';
import CreatedMainScene from './ProfileCreated/main';
import { getAvatarNameByPath } from '@/helpers/profile/avatar';

class CreateProfileScene extends Scene {
  progress = null;
  fontLoading = false;

  init() {
    store.dispatch('children/clearNewProfile');

    this.scene.add('mainGrade', GradeScene);
    this.scene.add('mainGender', GenderMainScene);
    this.scene.add('mainAvatar', AvatarsMainScene);
    this.scene.add('mainName', NameMainScene);
    this.scene.add('profileSummary', SummaryMainScene);
    this.scene.add('profileCreated', CreatedMainScene);
  }

  preload() {
    this.loadFonts().finally(() => this.loadImages());
  }

  loadFonts() {
    this.fontLoading = true;

    const font = new FontFaceObserver('Font006AllCapsBW');
    return font
      .load(null, 30_000)
      .finally(() => { this.fontLoading = false; });
  }

  loadImages() {
    this.load.on('complete', this.onLoadImagesComplete, this);

    const cfgGrade = EDC.Games.GradeSelect.Config;
    const cfgAvatar = EDC.Games.AvatarSelect.Config;

    BackgroundPreloader(this, cfgGrade.bgList);

    this.load.setPath('https://s3.amazonaws.com/images.growmindgrow/phaser-assets/images/');

    this.load.image('logo', 'OrbUp_Logo.png');

    this.load.setPath(
      'https://s3.amazonaws.com/images.growmindgrow/phaser-assets/images/views/CreateProfileFlow/GenderSelection/',
    );

    this.load.image('back-icon', 'back-icon.png');
    this.load.image('back-normal', 'back-normal.png');
    this.load.image('back-hover', 'back-hover.png');
    this.load.image('choose', 'choose.png');

    // Gender Selection
    this.load.image('boy-bg', 'boy-bg.png');
    this.load.image('title-frame', 'title-frame.png');
    this.load.image('title', 'title.png');
    this.load.image('boy', 'boy.png');
    this.load.image('boy-symbol-dark', 'boy-symbol-dark.png');
    this.load.image('boy-symbol-light', 'boy-symbol-light.png');
    this.load.image('boy-text-dark', 'boy-text-dark.png');
    this.load.image('boy-text-light', 'boy-text-light.png');

    this.load.image('girl-bg', 'girl-bg.png');
    this.load.image('girl', 'girl.png');
    this.load.image('girl-symbol-dark', 'girl-symbol-dark.png');
    this.load.image('girl-symbol-light', 'girl-symbol-light.png');
    this.load.image('girl-text-dark', 'girl-text-dark.png');
    this.load.image('girl-text-light', 'girl-text-light.png');

    // Avatar Selection
    this.load.setPath(
      'https://s3.amazonaws.com/images.growmindgrow/phaser-assets/images/views/CreateProfileFlow/AvatarSelection/',
    );

    this.load.image('window', 'window.png');
    this.load.image('avatar-title', 'title.png');
    this.load.image('type-separator', 'types/separator.png');
    this.load.image('select-button', 'select-button.png');
    this.load.image('preview', 'preview.png');
    this.load.image('choose-text-normal', 'Ok_Text.png');
    this.load.image('choose-text-light', 'choose-text-light.png');
    this.load.image('list-arrow-normal', 'list/list-arrow-normal.png');
    this.load.image('list-arrow-light', 'list/list-arrow-light.png');
    this.load.image('list-arrow-dark', 'list/list-arrow-dark.png');
    this.load.image('list-arrow-disabled', 'list/list-arrow-disabled.png');
    this.load.image('list-scroll-bar-bg', 'list/list-scroll-bar-bg.png');
    this.load.image('list-scroll-bar-caret', 'list/list-scroll-bar-caret.png');
    this.load.image('item-bg', 'list/item-background.png');
    this.load.image('item-border', 'list/frame.png');
    this.load.image('item-selected', 'list/item-selected.png');

    for (let i = 0; i < cfgAvatar.types.length; i++) {
      const data = cfgAvatar.types[i];
      this.load.image(`type-btn-${data.type}-normal`, data.imgNormal);
      this.load.image(`type-btn-${data.type}-light`, data.imgLight);
    }

    for (let i = 0; i < cfgAvatar.avatars.length; i++) {
      const avatar = cfgAvatar.avatars[i];
      const avatarName = getAvatarNameByPath(avatar.img);
      this.load.image(`avatar-${avatarName}`, avatar.img);
    }

    // Name Selection
    this.load.setPath(
      'https://s3.amazonaws.com/images.growmindgrow/phaser-assets/images/views/CreateProfileFlow/NameSelection/',
    );
    this.load.setPrefix('name-selection-');

    this.load.image('title', 'title.png');
    this.load.image('window', 'window.png');
    this.load.image('spin-slot', 'spin-slot.png');
    this.load.image('spin-left', 'spin-left.png');
    this.load.image('spin-right', 'spin-right.png');
    this.load.image('new-name-bg', 'new-name-bg.png');
    this.load.image('new-name-button', 'new-name-button.png');
    this.load.image('new-name-normal', 'new-name-normal.png');
    this.load.image('new-name-over', 'new-name-over.png');
    this.load.image('save-name-bg', 'save-name-bg.png');
    this.load.image('save-name-button', 'save-name-button.png');
    this.load.image('save-name-normal', 'save-name-normal.png');
    this.load.image('save-name-over', 'save-name-over.png');
    this.load.image('wand', 'wand.png');
    this.load.image('wand-bg-1', 'wand/background1.png');
    this.load.image('wand-bg-2', 'wand/background2.png');
    this.load.image('wand-fg-1', 'wand/foreground1.png');
    this.load.image('wand-ray-1', 'wand/ray1.png');
    this.load.image('wand-ray-2', 'wand/ray2.png');
    this.load.image('wand-ray-3', 'wand/ray3.png');
    this.load.image('wand-ray-4', 'wand/ray4.png');

    // Grade Selection
    this.load.setPath(
      'https://s3.amazonaws.com/images.growmindgrow/phaser-assets/images/views/CreateProfileFlow/GradeSelection/',
    );
    this.load.setPrefix('grade-selection-');

    this.load.image('answer-back', 'answer-back.png');
    this.load.image('answer-frame', 'answer-frame.png');
    this.load.image('answer-highlight', 'answer-highlight.png');
    this.load.image('answer-panel', 'answer-panel.png');
    this.load.image('grade--1', 'grade--1.png');
    this.load.image('grade-0', 'grade-0.png');
    this.load.image('grade-1', 'grade-1.png');
    this.load.image('grade-2', 'grade-2.png');
    this.load.image('grade-3', 'grade-3.png');
    this.load.image('grade-4', 'grade-4.png');
    this.load.image('grade-5', 'grade-5.png');
    this.load.image('grade-6', 'grade-6.png');
    this.load.image('title', 'title.png');

    // Profile Summary
    this.load.setPath(
      'https://s3.amazonaws.com/images.growmindgrow/phaser-assets/images/views/CreateProfileFlow/ProfileSummary/',
    );
    this.load.setPrefix('profile-summary-');

    this.load.image('background', 'background.jpg');
    this.load.image('title', 'title.png');
    this.load.image('title-bg', 'title-bg.png');
    this.load.image('frame', 'summary-frame.png');
    this.load.image('create-btn-normal', 'button-create-normal.png');
    this.load.image('create-btn-hover', 'button-create-hover.png');
    this.load.image('back-btn-normal', 'button-back-normal.png');
    this.load.image('back-btn-hover', 'button-back-hover.png');
    this.load.image('gender-bg', 'gender-bg.png');
    this.load.image('gender-girl', 'gender-girl.png');
    this.load.image('gender-boy', 'gender-boy.png');
    this.load.image('avatar-bg', 'avatar-bg.png');
    this.load.image('avatar-frame', 'avatar-frame.png');

    // Profile Created
    this.load.setPath(
      'https://s3.amazonaws.com/images.growmindgrow/phaser-assets/images/views/CreateProfileFlow/ProfileCreated/',
    );
    this.load.setPrefix('profile-created-');

    this.load.image('background', 'background.jpg');
    this.load.image('title', 'title.png');
    this.load.image('frame', 'frame.png');
    this.load.image('text', 'text.png');
    this.load.image('btn-continue-normal', 'btn-continue-normal.png');
    this.load.image('btn-continue-hover', 'btn-continue-hover.png');
    this.load.image('btn-another-way-normal', 'btn-another-way-normal.png');
    this.load.image('btn-another-way-hover', 'btn-another-way-hover.png');

    //
    this.load.setPath();
    this.load.setPrefix();
    this.load.start();

    this.addLoadListeners();
  }

  addLoadListeners() {
    const onLoadProgress = (value) => {
      this.progress = value;
    };
    this.load.on('progress', onLoadProgress);

    const onLoadComplete = () => {
      this.load.off(onLoadProgress);
      this.load.off(onLoadComplete);
    };
    this.load.on('complete', onLoadComplete);
  }

  onLoadImagesComplete() {
    this.load.off(this.onLoadImagesComplete, this);

    const url = new URL(window.location.href);
    const editParam = url.searchParams.get('edit');

    switch (editParam) {
      case 'grade':
        this.scene.start('mainGrade');
        break;
      case 'gender':
        this.scene.start('mainGender');
        break;
      case 'avatar':
        this.scene.start('mainAvatar');
        break;
      case 'name':
        this.scene.start('mainName');
        break;
      case 'summary':
        this.scene.start('profileSummary');
        break;
      default:
        this.scene.start('mainGrade');
        break;
    }
  }
}

EDC.Games.GradeSelect.Scenes.Preloader = CreateProfileScene;

export default CreateProfileScene;
